import styled, { css } from 'styled-components';

import { ButtonProps } from './types';

type CssRecord<T extends string> = Record<T, ReturnType<typeof css>>;
type ButtonComponentProps = {
  $variant: NonNullable<ButtonProps['variant']>;
  $isLoading: NonNullable<ButtonProps['isLoading']>;
};

const borderVariant = css`
  box-shadow: inset 0 0 0 2px currentColor;
`;

const variantStyles: CssRecord<ButtonComponentProps['$variant']> = {
  primary: css`
    color: var(--color-white);
    background-color: var(--color-numa-black--400);

    &:hover {
      background-color: var(--color-numa-black--300);
    }

    &:disabled {
      background-color: var(--color-numa-black--200);
      color: var(--color-white);
    }

    &:active {
      background-color: var(--color-numa-black--400);
    }
  `,
  secondary: css`
    color: var(--color-numa-black--400);
    background-color: var(--color-white);
    ${() => borderVariant}

    &:hover {
      box-shadow: inset 0 0 0 3px currentColor;
    }

    &:active {
      color: var(--color-white);
      background-color: var(--color-numa-black--400);
      box-shadow: inset 0 0 0 3px var(--color-numa-black-400);
    }
  `,
  negative: css`
    color: var(--color-numa-red--100);
    background-color: var(--color-white);
    ${() => borderVariant}

    &:hover {
      box-shadow: inset 0 0 0 3px currentColor;
      color: var(--color-numa-red--200);
    }

    &:active {
      color: var(--color-white);
      background-color: var(--color-numa-red--100);
      box-shadow: inset 0 0 0 3px var(--color-numa-red-100);
    }

    &:disabled {
      color: var(--color-numa-red--50);
      box-shadow: inset 0 0 0 3px currentColor;
    }
  `,
  link: css`
    color: var(--color-numa-black--400);
    background-color: transparent;
    text-decoration: underline;

    &:hover {
      color: var(--color-numa-black--300);
    }

    &:disabled {
      color: var(--color-numa-black--200);
    }
  `,
};

const wrapperStyles = css<ButtonComponentProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin: 0;
  padding: var(--spacing-4) var(--spacing-8);
  border: none;
  cursor: pointer;
  font: var(--font-body--medium);
  font-size: 18px;
  line-height: 21.42px;
  transition: var(--transition--default);
  transition-property: background-color, color;
  border-radius: 8px;

  @media screen and (min-width: 768px) {
    display: inline-flex;
  }

  svg {
    display: block;
  }

  &:disabled {
    color: var(--color-numa-black--250);
    pointer-events: none;
  }

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      pointer-events: none;
    `}

  ${({ $variant }) => variantStyles[$variant]}
`;

export const ButtonWrapper = styled.button<ButtonComponentProps>`
  ${wrapperStyles}
`;

export const LinkButtonWrapper = styled.a<ButtonComponentProps>`
  ${wrapperStyles}
`;

export const ChildContainer = styled.span<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`;

const Indicator = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const LoadingIndicator = styled(Indicator)``;
